import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { Chart } from '@frontend/charts';
import { DashboardWidget, DashboardWidgetFC } from '@frontend/grid-dashboard';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ContentLoader, Text } from '@frontend/design-system';
import { featureFlags } from '../../../feature-flags';
import { trackingIds } from '../../../tracking-ids';
import { useFetchARROIOOverview } from '../appointment-reminders/hooks/use-fetch-ar-roi-overview';
import { customROITimePeriods } from '../constants';
import { useHasRoiFeature } from '../hooks/use-has-roi-feature';
import { useFetchNSRROITimeline } from '../no-show-rate/hooks/use-fetch-nsr-roi-timeline';
import { CustomTimeHeader } from './custom-time-header';

/**
 * @dashboard-widget
 *
 * id: no-show-rate
 * title: No-Show Rate
 * description: Track no-show performance for your location with historical trends
 * icon: user
 */

export const NoShowRateWidget: DashboardWidgetFC = () => {
  const timePeriod = customROITimePeriods.lastMonth;
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);
  const { isFeatureEnabled } = useHasRoiFeature({ roi: 'noShowRate' });

  const filters = {
    startDate: timePeriod.value.startDate,
    endDate: timePeriod.value.endDate,
    locationIds: selectedLocationIds,
  };

  const { isLoading: isLoadingTimeline, rawData } = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType: NoShowRateByType.WEAVE_AVERAGE,
    },
    filters,
    isEnabled: isFeatureEnabled,
  });

  const { aggregated, isLoading: isLoadingNSRRate } = useFetchARROIOOverview({
    fetchNoShowRate: false,
    filters,
    forceDemoData: isDemoAccount,
    roiType: RoiType.WEAVE,
    isEnabled: isFeatureEnabled,
  });

  const trend = useMemo(() => {
    return (
      rawData?.data?.dates?.map(({ params }) => params?.find(({ name }) => name === 'SelectedLocations')?.value ?? 0) ||
      []
    );
  }, [rawData]);

  return (
    <DashboardWidget data-trackingid={trackingIds.roi.noShowRateWidget}>
      <DashboardWidget.Header>
        <CustomTimeHeader label={timePeriod.label} />
      </DashboardWidget.Header>
      <DashboardWidget.Content css={styles.widgetContent}>
        <Text style={{ fontSize: theme.font.size.h2 }} weight='bold'>
          {formatters.appendPercent(
            isDemoAccount ? Math.abs(aggregated.noShowRatePercentage) : aggregated.noShowRatePercentage
          )}
        </Text>
        <div css={styles.chartContainer}>
          <Chart.TinyAreaChart data={trend} color={theme.colors.critical30} />
        </div>
        <ContentLoader show={isLoadingTimeline || isLoadingNSRRate} size='small' />
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

NoShowRateWidget.config = {
  size: 'small-narrow',
  feature: 'analytics',
};

const styles = {
  widgetContent: css`
    position: relative;
    overflow: hidden;
  `,

  chartContainer: css`
    height: ${theme.spacing(5)};
    width: 100%;
  `,
};
